import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileDialog } from "../components/mobile";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Network, MapPin, Sparkles, Inbox, Send, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const KINDS = [
  ["associates", "Taking associates"],
  ["overflow_pupils", "Overflow pupils"],
  ["partnership", "Local partnership"],
  ["mentoring", "Mentoring places"],
  ["franchise", "Franchise"],
  ["area_expansion", "Area expansion"],
];
const KIND_LABEL = Object.fromEntries(KINDS);

/** NetworkBoard — post and apply to opportunities. Relocation flow is just a kind filter. */
export default function NetworkBoard() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [items, setItems] = useState([]);
  const [filterKind, setFilterKind] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [creating, setCreating] = useState(false);
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);

  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (filterKind) q.set("kind", filterKind);
      if (filterArea) q.set("area", filterArea);
      const [list, s, r] = await Promise.all([
        axios.get(`${API}/api/network-board?${q}`, { withCredentials: true }),
        axios.get(`${API}/api/network-board/applications/sent`, { withCredentials: true }),
        axios.get(`${API}/api/network-board/applications/received`, { withCredentials: true }),
      ]);
      setItems(list.data.items || []); setSent(s.data.items || []); setReceived(r.data.items || []);
    } catch { toast.error("Couldn't load the board."); }
  };
  useEffect(() => { load(); }, [filterKind, filterArea]);

  const apply = async (opp) => {
    const note = prompt(`Apply to "${opp.title}"?\nShort note (optional):`) ?? "";
    try {
      await axios.post(`${API}/api/network-board/${opp.id}/apply`, { note: note.slice(0, 400) }, { withCredentials: true });
      toast.success("Applied — the poster will see your passport.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't apply."); }
  };

  const decide = async (appId, accept) => {
    try {
      await axios.post(`${API}/api/network-board/applications/${appId}/decide`, { accept }, { withCredentials: true });
      toast.success(accept ? "Accepted." : "Declined.");
      await load();
    } catch { toast.error("Couldn't decide."); }
  };

  const sentMap = new Map(sent.map((s) => [s.opportunity_id, s]));

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="network-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"><ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-1.5"><Network className="w-4 h-4" /> Network Board</h1>
            <p className="text-[11px] text-slate-500">Opportunities, associates, overflow, partnerships</p>
          </div>
          <button data-testid="network-new" onClick={() => setCreating(true)} className="p-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500"><Plus className="w-4 h-4" /></button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
        {/* Filters */}
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          <button data-testid="filter-all" onClick={() => setFilterKind("")} className={`flex-shrink-0 px-2.5 py-1 text-[11px] font-bold rounded-full border ${!filterKind ? "bg-slate-900 text-white border-slate-900" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"}`}>All</button>
          {KINDS.map(([k, l]) => (
            <button key={k} data-testid={`filter-${k}`} onClick={() => setFilterKind(k)} className={`flex-shrink-0 px-2.5 py-1 text-[11px] font-bold rounded-full border ${filterKind === k ? "bg-slate-900 text-white border-slate-900" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"}`}>{l}</button>
          ))}
        </div>
        <input data-testid="filter-area" value={filterArea} onChange={(e) => setFilterArea(e.target.value)} placeholder="Area (e.g. Reading) — leave blank for all"
          className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white" />

        {items.length === 0 ? (
          <p className="text-center text-xs text-slate-400 py-6">No opportunities yet — post the first one.</p>
        ) : (
          <ul data-testid="network-list" className="space-y-2">
            {items.map((o) => {
              const sentApp = sentMap.get(o.id);
              return (
                <li key={o.id} data-testid={`network-item-${o.id}`} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">{KIND_LABEL[o.kind] || o.kind}</span>
                    {o.area && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {o.area}</span>}
                    <span className="ml-auto text-[10px] text-slate-400">{new Date(o.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm font-extrabold text-slate-900 dark:text-white">{o.title}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 whitespace-pre-wrap">{o.body}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[10px] text-slate-400">by {o.author_name || "Instructor"}</span>
                    {sentApp ? (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        sentApp.status === "accepted" ? "bg-emerald-100 text-emerald-800" :
                        sentApp.status === "declined" ? "bg-red-100 text-red-800" :
                        "bg-slate-100 text-slate-600"}`}>Applied · {sentApp.status}</span>
                    ) : (
                      <button data-testid={`apply-${o.id}`} onClick={() => apply(o)} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 flex items-center gap-1"><Send className="w-3 h-3" /> Apply with passport</button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Received applications */}
        {received.length > 0 && (
          <section className="mt-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
            <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Inbox className="w-3 h-3" /> Applications to your posts</p>
            <ul className="space-y-1.5">
              {received.map((a) => (
                <li key={a.id} data-testid={`received-${a.id}`} className="rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-2 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-900 dark:text-white">{a.applicant_name}</p>
                    {a.note && <p className="text-[11px] text-slate-500 truncate">{a.note}</p>}
                  </div>
                  {a.status === "pending" ? (
                    <>
                      <button data-testid={`received-accept-${a.id}`} onClick={() => decide(a.id, true)} className="text-[10px] font-bold px-2 py-1 rounded bg-emerald-600 text-white">Accept</button>
                      <button data-testid={`received-decline-${a.id}`} onClick={() => decide(a.id, false)} className="text-[10px] font-bold px-2 py-1 rounded bg-white border border-slate-200 text-slate-700">Decline</button>
                    </>
                  ) : (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${a.status === "accepted" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{a.status}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [kind, setKind] = useState("overflow_pupils");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [area, setArea] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and body required."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/network-board`, { kind, title, body, area }, { withCredentials: true });
      toast.success("Posted.");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't post."); }
    finally { setSaving(false); }
  };

  return (
    <MobileDialog
      bareContent
      open
      onClose={onClose}
      testid="network-create-modal-wrap"
    >
      <div data-testid="network-create-modal" className="bg-white dark:bg-slate-900 rounded-3xl sm:rounded-2xl w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">New opportunity</p>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><X className="w-4 h-4" /></button>
        </div>
        <div>
          <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Type</label>
          <div className="mt-1 grid grid-cols-2 gap-1.5">
            {KINDS.map(([k, l]) => (
              <button key={k} data-testid={`new-kind-${k}`} onClick={() => setKind(k)} className={`px-2 py-1.5 text-[11px] font-bold rounded-lg border ${kind === k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"}`}>{l}</button>
            ))}
          </div>
        </div>
        <input data-testid="new-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short title" maxLength={120}
          className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white" />
        <textarea data-testid="new-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="What are you offering? Who's it for?" rows={5} maxLength={1200}
          className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white resize-none" />
        <input data-testid="new-area" value={area} onChange={(e) => setArea(e.target.value)} placeholder="Area (optional)"
          className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white" />
        <button data-testid="new-save" onClick={save} disabled={saving} className="w-full px-4 py-2.5 text-sm font-extrabold rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60">{saving ? "Posting…" : "Post opportunity"}</button>
        <p className="text-[10px] text-slate-400 text-center">Factual, professional wording only. Guaranteed-pass claims will be rejected.</p>
      </div>
    </MobileDialog>
  );
}
